import type { ArticleV2 as ArticleV2PageType } from 'types/capi/article';

import usePageData from '~/hooks/usePageData';
import useStructuredData from '~/hooks/useStructuredData';
import ArticleCarousel from '~/modules/ArticleCarousel';
import RecentFeatures from '~/modules/RecentFeatures';
import ContentBlock from '~/modules/ste/ContentBlock';
import EmbedBlock from '~/modules/ste/EmbedBlock';
import ImageGrid from '~/modules/ste/ImageGrid';
import ProductList from '~/modules/ste/ProductList';

import CommissionDisclaimer from './CommissionDisclaimer';
import { Footer, Header } from './HeaderFooter';
import HeaderImage from './HeaderImage';

export default function ArticleV2() {
  const {
    title,
    core_images,
    display_header,
    author,
    dates,
    tags,
    article_type,
    _schemas
  } = usePageData<ArticleV2PageType>();

  useStructuredData(_schemas);

  return (
    <>
      <CommissionDisclaimer />
      <div className="flex w-full flex-col gap-10 bg-grey-tile">
        <div>
          {display_header && (
            <HeaderImage images={core_images} articleType={article_type} />
          )}
          <Header
            author={author}
            title={title}
            publishedDate={dates.published}
            modifiedDate={dates.modified}
            tags={tags}
          />
        </div>
        <Modules />
        {article_type === 'news' ? <NewsFooterContent /> : <FooterContent />}
      </div>
    </>
  );
}

function Modules() {
  const { modules, article_type } = usePageData<ArticleV2PageType>();

  return (
    <>
      {modules.map((module, index) => {
        switch (module.type) {
          case 'image-grid':
            return <ImageGrid key={index} items={module.items} />;
          case 'embed':
            return <EmbedBlock key={index} embed={module} />;
          case 'product-list':
            return <ProductList key={index} {...module} />;
          case 'content-block':
            return (
              <ContentBlock
                key={index}
                {...module}
                narrow={article_type === 'feature'}
              />
            );
          default:
            return null;
        }
      })}
    </>
  );
}

function FooterContent() {
  const { author, title, dates, tags, recent_features, latest_news } =
    usePageData<ArticleV2PageType>();

  return (
    <div>
      <div className="w-full bg-black">
        <Footer
          author={author}
          title={title}
          publishedDate={dates.published}
          modifiedDate={dates.modified}
          tags={tags}
        />
        <div className="mx-auto h-px w-8/12 max-w-[600px] bg-white" />
        {recent_features && (
          <RecentFeatures
            title="Discover more features"
            items={recent_features}
          />
        )}
      </div>
      {latest_news && (
        <ArticleCarousel items={latest_news} title="Related articles" />
      )}
    </div>
  );
}

function NewsFooterContent() {
  const { author, title, dates, tags, recent_features, latest_news } =
    usePageData<ArticleV2PageType>();

  return (
    <div>
      <Footer
        author={author}
        title={title}
        publishedDate={dates.published}
        modifiedDate={dates.modified}
        tags={tags}
        variant="light"
      />
      {latest_news && (
        <ArticleCarousel items={latest_news} title="Related articles" />
      )}
      <div className="w-full bg-black">
        {recent_features && (
          <RecentFeatures
            title="Discover more features"
            items={recent_features}
          />
        )}
      </div>
    </div>
  );
}
